import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import {
  getBoundingClientRect,
  invokeFunction,
  isHTMLElement,
  isIE,
  useForceUpdate,
  useResizeObserver,
  getClosestPointOnRect
} from '../../utilities';

import { defaultGlobalPosition, defaultConnectedPosition, getMemoizedPositions } from './config';
import OverlayOutlet from './Outlet';
import OverlayTrigger from './Trigger';
import useOverlayState from './useOverlayState';
import useOverlayConfig from './useOverlayConfig';

import './style.scss';

export { OverlayOutlet, OverlayTrigger, useOverlayState, useOverlayConfig };

const Overlay = React.forwardRef(
  (
    {
      children,
      name,
      outlet: _outlet = document.body,
      origin: _origin,
      viewport,
      positions: _positions,
      positionStrategy = 'connected', // connected, global
      hasBackdrop = true,
      hasHandle = true,
      containerStyle,
      backdropStyle,
      handleShadowStyle,
      handleStyle: _handleStyle,
      overlayStyle: _overlayStyle,
      onBackdropClick = () => {},
      onClose = () => {},
      closeOnBackdropClick = false,
      scrolling,
      scrollStrategy = 'reposition', // reposition, close, block
      ...otherProps
    },
    ref
  ) => {
    const getHTMLElement = target => {
      if (!target) {
        return null;
      }

      return isHTMLElement(target) ? target : target.current;
    };

    const getElements = () => {
      let origin = getHTMLElement(_origin);
      let outlet = getHTMLElement(_outlet);
      let overlayOutlet;

      if (OverlayOutlet.ident(outlet)) {
        overlayOutlet = outlet;
        outlet = outlet.getHTMLElement();
      }

      return {
        origin,
        outlet,
        overlayOutlet
      };
    };

    const { origin, outlet, overlayOutlet } = getElements();

    const forceUpdate = useForceUpdate();

    useEffect(() => {
      const elements = getElements();

      if (origin !== elements.origin || outlet !== elements.outlet || overlayOutlet !== elements.overlayOutlet) {
        forceUpdate();
      }
    }, []);

    const [overlayPosition, setOverlayPosition] = useState({ x: -1000, y: 0 });
    const [overlayOffset, setOverlayOffset] = useState({ x: 0, y: 0 });
    const [handlePosition, setHandlePosition] = useState({ x: -1000, y: 0 });
    const [handleOffset, setHandleOffset] = useState({ x: 0, y: 0 });
    const [hidden, setHidden] = useState(false);
    const [transparent, setTransparent] = useState(false);
    const [id] = useState(Math.random());
    const containerRef = useRef();
    const overlayRef = useRef();

    const getPositions = () => {
      const defaultPosition = positionStrategy === 'global' ? defaultGlobalPosition : defaultConnectedPosition;

      const positions = _positions === undefined ? defaultPosition : _positions;

      return getMemoizedPositions(positions, positionStrategy);
    };

    const positions = getPositions();

    useImperativeHandle(ref, () => ({
      get containerElement() {
        return containerRef.current;
      },

      get overlayElement() {
        return overlayRef.current;
      }
    }));

    useEffect(() => {
      if (overlayOutlet) {
        overlayOutlet.register(id, {
          name,
          close: onClose
        });

        return () => overlayOutlet.unregister(id);
      }
    }, []);

    const getPositionOutput = ({ position, originRect, overlayRect, viewportRect }) => {
      const { originX, originY, overlayX, overlayY, offsetX, offsetY } = position;

      const anchorPoint = {};
      const _overlayPosition = {};
      const _overlayOffset = {};
      const overlayPoint = {};

      switch (originX) {
        case 'start':
          anchorPoint.x = originRect.x;
          break;
        case 'end':
          anchorPoint.x = originRect.x + originRect.width;
          break;
        case 'center':
        default:
          anchorPoint.x = originRect.x + originRect.width / 2;
          break;
      }

      anchorPoint.x = anchorPoint.x - viewportRect.x;
      _overlayPosition.x = anchorPoint.x + offsetX;

      switch (originY) {
        case 'top':
          anchorPoint.y = originRect.y;
          break;
        case 'bottom':
          anchorPoint.y = originRect.y + originRect.height;
          break;
        case 'center':
        default:
          anchorPoint.y = originRect.y + originRect.height / 2;
          break;
      }

      anchorPoint.y = anchorPoint.y - viewportRect.y;
      _overlayPosition.y = anchorPoint.y + offsetY;

      switch (overlayX) {
        case 'start':
          _overlayOffset.x = 0;
          overlayPoint.x = _overlayPosition.x;
          break;
        case 'end':
          _overlayOffset.x = '-100%';
          overlayPoint.x = _overlayPosition.x - overlayRect.width;
          break;
        case 'center':
        default:
          _overlayOffset.x = '-50%';
          overlayPoint.x = _overlayPosition.x - overlayRect.width / 2;
          break;
      }

      switch (overlayY) {
        case 'top':
          _overlayOffset.y = 0;
          overlayPoint.y = _overlayPosition.y;
          break;
        case 'bottom':
          _overlayOffset.y = '-100%';
          overlayPoint.y = _overlayPosition.y - overlayRect.height;
          break;
        case 'center':
        default:
          _overlayOffset.y = '-50%';
          overlayPoint.y = _overlayPosition.y - overlayRect.height / 2;
          break;
      }

      let _handlePosition = { ..._overlayPosition };
      const _handleOffset = { x: '-50%', y: '-50%' };

      if (hasHandle) {
        // The handle should be placed at the position, which is a point on the edges of the
        // overlay and at the same time is the closest to the origin point.

        const positionHandle = () => {
          const point = getClosestPointOnRect(anchorPoint, [
            overlayPoint,
            {
              x: overlayPoint.x + overlayRect.width,
              y: overlayPoint.y + overlayRect.height
            }
          ]);

          if (point !== null) {
            _handlePosition = point;
          }
        };

        positionHandle();
      }

      const { x, y } = overlayPoint;
      const nonnegative = number => Math.max(number, 0);
      const overflow = {
        left: nonnegative(0 - x),
        right: nonnegative(x + overlayRect.width - viewportRect.right),
        top: nonnegative(0 - y),
        bottom: nonnegative(y + overlayRect.height - viewportRect.bottom)
      };

      const subtractOverflows = (target, ...overflows) => overflows.reduce((res, overflow) => res - overflow, target);

      const visibleWidth = subtractOverflows(overlayRect.width, overflow.left, overflow.right);
      const visibleHeight = subtractOverflows(overlayRect.height, overflow.top, overflow.bottom);
      const visibleArea = visibleWidth * visibleHeight;
      const overlayArea = overlayRect.width * overlayRect.height;

      return {
        overlayPosition: _overlayPosition,
        overlayOffset: _overlayOffset,
        handlePosition: _handlePosition,
        handleOffset: _handleOffset,
        overlayPoint,
        overflow,
        visibleArea,
        fitsWithinViewport: visibleArea === overlayArea
      };
    };

    const applyPositionOutput = ({
      overlayPosition: _overlayPosition,
      overlayOffset: _overlayOffset,
      handlePosition: _handlePosition,
      handleOffset: _handleOffset
    }) => {
      setOverlayPosition(_overlayPosition);
      setOverlayOffset(_overlayOffset);

      if (hasHandle) {
        setHandlePosition(_handlePosition);
        setHandleOffset(_handleOffset);
      }
    };

    const pushOverlayOnScreen = byproducts => {
      const { positionOutput, overlayRect, viewportRect } = byproducts;
      const {
        handlePosition: _handlePosition,
        overflow,
        overlayPoint,
        overlayPosition: _overlayPosition
      } = positionOutput;

      let pushX = 0,
        pushY = 0;
      const pushPosition = ({ x, y }) => ({ x: x + pushX, y: y + pushY });

      if (overlayRect.width <= viewportRect.width) {
        pushX = overflow.left || -overflow.right;
      } else {
        pushX = -overlayPoint.x;
      }

      if (overlayRect.height <= viewportRect.height) {
        pushY = overflow.top || -overflow.bottom;
      } else {
        pushY = -overlayPoint.y;
      }

      return {
        ...positionOutput,
        overlayPosition: pushPosition(_overlayPosition),
        handlePosition: hasHandle ? handlePosition : pushPosition(_handlePosition)
      };
    };

    const getEffectiveViewport = () => (viewport ? viewport : outlet);

    const positionOverlay = () => {
      if (!outlet || (!origin && positionStrategy === 'connected') || !containerRef.current || !overlayRef.current) {
        return;
      }

      if (positionStrategy === 'global') {
        const position = {
          ...defaultGlobalPosition,
          ...positions[0]
        };
        const { overlayX, overlayY, offsetX, offsetY } = position;
        setOverlayPosition({ x: overlayX, y: overlayY });
        setOverlayOffset({ x: offsetX, y: offsetY });
        return;
      }

      const originRect = getBoundingClientRect(origin);
      const overlayRect = getBoundingClientRect(overlayRef.current);
      const viewportRect = getBoundingClientRect(getEffectiveViewport());

      let fallback;

      for (const position of positions) {
        const positionOutput = getPositionOutput({
          position,
          originRect,
          overlayRect,
          viewportRect
        });

        if (positionOutput.fitsWithinViewport) {
          applyPositionOutput(positionOutput);
          return;
        }

        if (!fallback || positionOutput.visibleArea > fallback.visibleArea) {
          fallback = positionOutput;
        }
      }

      const pushedOutput = pushOverlayOnScreen({
        positionOutput: fallback,
        overlayRect,
        viewportRect
      });

      applyPositionOutput(pushedOutput);
    };

    useEffect(() => {
      positionOverlay();
    }, [positions, positionStrategy, outlet, origin, containerRef.current, overlayRef.current]);

    useResizeObserver(outlet, positionOverlay);
    useResizeObserver(overlayRef.current, positionOverlay);

    useEffect(() => {
      const inIE = isIE();

      if (scrolling) {
        let timer = null;

        const reposition = () => {
          if (!outlet || (!origin && positionStrategy === 'connected')) {
            return;
          }

          setHidden(true);

          if (timer) {
            clearTimeout(timer);
          }

          const _reposition = () => {
            const originRect = getBoundingClientRect(origin);
            const viewportRect = getBoundingClientRect(getEffectiveViewport());

            if (originRect.top < viewportRect.top || originRect.bottom > viewportRect.bottom) {
              setHidden(true);
            } else {
              if (!inIE) {
                positionOverlay();
                setHidden(false);
              } else {
                setHidden(false);
                setTransparent(true);
                positionOverlay();
                setTransparent(false);
              }
            }
          };

          timer = setTimeout(() => {
            _reposition();
            timer = null;
          }, 250);
        };

        const handler = () => {
          switch (scrollStrategy) {
            case 'close':
              invokeFunction(onClose);
              break;
            case 'reposition':
              reposition();
              break;
            default:
              break;
          }
        };

        const dispose = scrolling(handler);

        return () => {
          dispose();
          if (timer) {
            clearTimeout(timer);
          }
        };
      }
    }, [origin, outlet, viewport, scrolling, scrollStrategy]);

    const handleBackdropClick = useCallback(
      e => {
        if (closeOnBackdropClick) {
          onClose(e);
        }

        onBackdropClick(e);
      },
      [onBackdropClick, onClose, closeOnBackdropClick]
    );

    const overlayEventsHandlers = Object.keys(otherProps)
      .filter(key => /^onOverlay[a-zA-Z]+/.test(key))
      .reduce(
        (res, key) => ({
          ...res,
          [key.replace(/Overlay/, '')]: otherProps[key]
        }),
        {}
      );

    const containerClasses = classNames({
      'up-overlay-container': true,
      hidden,
      transparent
    });

    const overlayStyle = {
      top: overlayPosition.y,
      left: overlayPosition.x,
      transform: `translate(${overlayOffset.x}, ${overlayOffset.y})`,
      ..._overlayStyle
    };

    const handleStyle = {
      top: handlePosition.y,
      left: handlePosition.x,
      transform: `translate(${handleOffset.x}, ${handleOffset.y}) rotate(45deg)`,
      ..._handleStyle
    };

    return outlet
      ? ReactDOM.createPortal(
          <div ref={containerRef} className={containerClasses} style={containerStyle}>
            {hasBackdrop && <div className="up-overlay-backdrop" onClick={handleBackdropClick} style={backdropStyle} />}
            {hasHandle && <div className="up-overlay-handle-shadow" style={{ ...handleStyle, ...handleShadowStyle }} />}
            <div className="up-overlay" ref={overlayRef} style={overlayStyle} {...overlayEventsHandlers}>
              {children}
            </div>
            {hasHandle && <div className="up-overlay-handle" style={handleStyle} />}
          </div>,
          outlet
        )
      : null;
  }
);

export default Overlay;
